import React, { useState } from "react";
import BanAgentMenu from "./BanAgentMenu";
import FlagImg from "../agent-tender/FlagImg";
import ConfirmationModal from "../club/ConfirmationModal";
import {
  STATUS_TONE,
  agentNoteOf,
  agentProfileLinkOf,
  ageOf,
  bucketOf,
  documentNameOf,
  experienceYearsOf,
  footOf,
  formatAmount,
  formatYears,
  heightOf,
  initialsOf,
  isFreeAgentPitch,
  isStaffTender,
  licenceOf,
  marketValueOf,
  nationalitiesOf,
  playerClubOf,
  playerPositionOf,
  profileLinkOf,
  requestPositionOf,
  timeLeftOf,
  transferFeeOf,
} from "./pitchFields";

/**
 * How much of the club's pick-up window is left, as the short value that sits
 * beside a label — the same shape as the countdown on an active card.
 *
 * The DEADLINE is the backend's (`reopen_deadline`, anchored on
 * Pitch.expired_at + pitch_lifecycle.PITCH_REOPEN_WINDOW). Only the wording of
 * the difference happens here — the frontend never knows the 14 and never
 * decides whether the window is open; that verdict is `pitch.can_reopen`.
 *
 * Returns null when there is no deadline or it has passed, so a caller can say
 * "closed" rather than print "0 minutes".
 */
export const formatReopenTimeLeft = (deadlineIso, now = Date.now()) => {
  if (!deadlineIso) return null;
  const remainingMs = new Date(deadlineIso).getTime() - now;
  if (Number.isNaN(remainingMs) || remainingMs <= 0) return null;

  const minutes = Math.floor(remainingMs / 60000);
  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"}`;
};

const STATUS_CHIP = {
  accepted: { label: "Accepted", className: "bg-[#D1FAE5] text-[#047857]" },
  rejected: { label: "Rejected", className: "bg-[#FEE2E2] text-[#B91C1C]" },
  deal: { label: "Deal", className: "bg-[#EDE9FE] text-[#6D28D9]" },
  signed: { label: "Signed", className: "bg-[#EDE9FE] text-[#6D28D9]" },
  external_deal: { label: "Deal", className: "bg-[#EDE9FE] text-[#6D28D9]" },
};

/**
 * An image that falls back to its placeholder instead of to a broken icon.
 *
 * Not cosmetic: player photos, club logos, agent avatars and documents are
 * served as PRESIGNED S3 URLs that EXPIRE (inventory 6.1a). A card left open,
 * or a cached response, therefore reaches a dead URL sooner or later, and the
 * browser's broken-image glyph is the one thing on this surface that no data
 * fix can prevent.
 */
const SafeImage = ({ src, alt, className, fallback = null }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return fallback;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
};

const SquareAvatar = ({ src, alt, initials, size }) => (
  <SafeImage
    src={src}
    alt={alt}
    className={`${size} flex-shrink-0 rounded-xl object-cover`}
    fallback={
      <div
        className={`${size} flex flex-shrink-0 items-center justify-center rounded-xl bg-[#E9EEF6] text-[15px] font-bold text-[#6B7280]`}
        aria-hidden={initials ? undefined : "true"}
      >
        {initials}
      </div>
    }
  />
);

/** Label left, value right, one full-width line with a hairline under it. */
const FactRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-[#F4F5F7] px-3 py-2.5">
    <span className="flex-shrink-0 text-[14px] font-medium text-[#6B7280]">
      {label}
    </span>
    <span className="min-w-0 break-words text-right text-[14px] font-semibold text-[#111827]">
      {value}
    </span>
  </div>
);

const PitchCard = ({
  pitch,
  tender,
  formatPosition,
  canAcceptPitches,
  handleAcceptReject,
  handleReopen,
  handleBanAgent,
  canBanAgents,
  navigate,
  compact = false,
  onRequestScouting,
}) => {
  const isStaff = isStaffTender(tender);
  // A pitch the club has not decided on yet. 'scouting' is the same live pitch
  // with a longer clock, so it carries the SAME actions as 'active'.
  const isUndecided = pitch?.status === "active" || pitch?.status === "scouting";
  // Exactly the three statuses V1 offered "Go to Inbox" for. `external_deal`
  // also lands in the Deals bucket server-side, but it is a deal struck OUTSIDE
  // the platform and there is no evidence a `pitch-<id>` channel exists for it,
  // so it deliberately keeps the chip and not the button.
  const inInbox = ["accepted", "deal", "signed"].includes(pitch?.status);
  // Counter comes from the backend, never from the frontend's own arithmetic:
  // the limit is enforced there (pitch_lifecycle.PITCH_SCOUTING_MAX) and a
  // second copy of the number here would be a second truth.
  const scoutingCount = pitch?.scouting_count || 0;
  const scoutingMax = pitch?.scouting_max ?? 3;
  const canScoutAgain = scoutingCount < scoutingMax;

  const isExpired = pitch?.status === "expired";
  const canReopen = isExpired && pitch?.can_reopen === true;
  const reopenTimeLeft = isExpired
    ? formatReopenTimeLeft(pitch?.reopen_deadline)
    : null;
  // A response served BEFORE the backend field exists says nothing about the
  // window — and "nothing" is not "closed".
  const reopenWindowClosed = isExpired && pitch?.can_reopen === false;

  const isFreeAgent = isFreeAgentPitch(pitch, tender);
  const tone = STATUS_TONE[bucketOf(pitch?.status)] || STATUS_TONE.active;
  const chip = STATUS_CHIP[pitch?.status];

  const name = isStaff
    ? pitch?.staff_name || "Name not available"
    : pitch?.player?.name || "Name not available";
  const photo = isStaff ? pitch?.staff_image : pitch?.player?.image;
  // The player's OWN position (`player.position`), not the one the request asks
  // for. Both `player_position` and `tender_position` carry `tender.position`,
  // which is why every V1 card printed the request's position under the
  // player's name. On a staff card the role IS the request's position.
  const rawSubLine = isStaff ? requestPositionOf(pitch) : playerPositionOf(pitch);
  const subLine = rawSubLine ? formatPosition(rawSubLine) : null;
  // All citizenships (L-FE-1); `nationality` keeps the old single-value meaning for the row guards.
  const nationalities = nationalitiesOf(pitch);
  const nationality = nationalities[0] || null;
  const club = isStaff ? null : playerClubOf(pitch);
  const experienceYears = isStaff ? experienceYearsOf(pitch) : null;

  const transferFee = transferFeeOf(pitch);
  const timeLeft = isUndecided ? timeLeftOf(pitch) : null;
  const note = agentNoteOf(pitch);

  const rows = [];
  const age = ageOf(pitch, tender);
  if (age !== null) rows.push({ label: "Age", value: formatYears(age) });
  if (isStaff) {
    const licence = licenceOf(pitch);
    if (licence) rows.push({ label: "Licence", value: licence });
    const documentName = documentNameOf(pitch);
    if (documentName) rows.push({ label: "Document", value: documentName });
  } else {
    const foot = footOf(pitch);
    if (foot) rows.push({ label: "Foot", value: foot });
    const height = heightOf(pitch);
    if (height !== null) rows.push({ label: "Height", value: `${height} cm` });
    const marketValue = formatAmount(
      marketValueOf(pitch),
      pitch?.player?.market_value_currency,
    );
    if (marketValue) rows.push({ label: "Market value", value: marketValue });
  }

  const agentName = pitch?.applied_by_agent?.name || null;
  const agencyName = pitch?.agency?.name || null;
  const agentImage = pitch?.applied_by_agent?.profile_picture_url || null;
  // Both links open in a NEW TAB, the way "Player profile" always has: the club
  // is halfway through deciding a list of pitches and must not lose its place
  // to a look at one profile.
  const agentProfilePath = agentProfileLinkOf(pitch);
  const profileLink = profileLinkOf(pitch, tender);
  const openProfile = (path) => window.open(path, "_blank");

  // The avatar and the two names of the "Applied by agent" row, written once so
  // the linked and the unlinked variant can never drift apart.
  const agentValue = (
    <>
      <SafeImage
        src={agentImage}
        alt=""
        className="h-[26px] w-[26px] flex-shrink-0 rounded-full object-cover"
        fallback={
          <span className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full bg-[#E9EEF6] text-[11px] font-bold text-[#6B7280]">
            {initialsOf(agentName).charAt(0)}
          </span>
        }
      />
      <span className="flex min-w-0 flex-col items-end">
        <span
          className={`truncate text-[14px] font-semibold ${
            agentProfilePath ? "text-[#2183FE]" : "text-[#374151]"
          }`}
        >
          {agentName}
        </span>
        {agencyName ? (
          <span className="truncate text-[12px] text-[#6B7280]">{agencyName}</span>
        ) : null}
      </span>
    </>
  );

  const showDecisionActions = isUndecided && canAcceptPitches;
  const showInboxButton = inInbox;

  const goToInbox = () =>
    navigate("/inbox", {
      state: { channelId: `pitch-${pitch.id}`, channelType: "messaging" },
    });

  const decisionButtons = (
    <>
      {canScoutAgain ? (
        <button
          type="button"
          onClick={() => onRequestScouting(pitch)}
          title="Send this pitch to scouting. Each send gives you 3 more days."
          className={`min-h-[46px] rounded-[10px] border border-[#F59E0B] bg-white px-4 text-[14px] font-semibold text-[#B45309] hover:bg-[#FFFBEB] ${
            compact ? "flex-1" : ""
          }`}
        >
          Scouting
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => handleAcceptReject(pitch, "reject")}
        className={`min-h-[46px] rounded-[10px] border border-[#FCA5A5] bg-white px-4 text-[14px] font-semibold text-[#DC2626] hover:bg-[#FEF2F2] ${
          compact ? "flex-1" : ""
        }`}
      >
        Reject
      </button>
    </>
  );

  const acceptButton = (
    <button
      type="button"
      onClick={() => handleAcceptReject(pitch, "accept")}
      className={`min-h-[46px] rounded-[10px] border border-[#2183FE] bg-[#2183FE] px-5 text-[14px] font-semibold text-white hover:bg-[#1668D9] ${
        compact ? "w-full" : ""
      }`}
    >
      Accept
    </button>
  );

  const inboxButton = (
    <button
      type="button"
      onClick={goToInbox}
      className={`min-h-[46px] rounded-[10px] border border-[#2183FE] bg-[#2183FE] px-[18px] text-[14px] font-semibold text-white hover:bg-[#1668D9] ${
        compact ? "w-full" : ""
      }`}
    >
      Go to Inbox
    </button>
  );

  return (
    <div
      className={`rounded-[14px] border border-[#E5E7EB] border-l-4 bg-white shadow-[0_1px_3px_rgba(17,24,39,0.06)] ${
        tone.accent
      } ${compact ? "p-4" : "p-5"} ${pitch?.status === "rejected" ? "opacity-90" : ""}`}
    >
      <div className="flex flex-wrap items-start gap-3">
        {/* Identity */}
        <div className="flex min-w-0 flex-[1_1_250px] gap-3.5">
          <SquareAvatar
            src={photo}
            alt={name}
            initials={initialsOf(name)}
            size="h-[52px] w-[52px]"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <h3 className="m-0 break-words text-[16px] font-bold text-[#111827]">
                {name}
              </h3>
              {isFreeAgent ? (
                <span
                  title="No transfer fee. Free agent player."
                  className="whitespace-nowrap rounded-full bg-[#2183FE] px-2.5 py-[3px] text-[11px] font-bold text-white"
                >
                  ★ Free Agent
                </span>
              ) : null}
            </div>

            {subLine || nationality ? (
              <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
                {subLine ? (
                  <span className="truncate text-[14px] text-[#374151]">
                    {subLine}
                  </span>
                ) : null}
                {subLine && nationality ? (
                  <span aria-hidden="true" className="flex-shrink-0 text-[#9CA3AF]">
                    ·
                  </span>
                ) : null}
                {/* The platform's own image flag (used on 15 surfaces), not a
                    second one: it resolves the API's `image_url`, then the
                    country code, then the name, and renders NOTHING when it
                    cannot resolve any of them. Never an emoji, which Windows
                    draws as a letter pair. */}
                {nationalities.map((nat) => (
                  <FlagImg
                    key={nat.code || nat.name}
                    country={nat}
                    className="h-[13px] w-[18px] rounded-[2px]"
                  />
                ))}
                {nationality ? (
                  <span className="flex-shrink-0 truncate text-[14px] text-[#374151]">
                    {nationalities.map((nat) => nat.name).join(" / ")}
                  </span>
                ) : null}
              </div>
            ) : null}

            {club ? (
              <div className="mt-2 flex min-w-0 items-center gap-2">
                <SafeImage
                  src={club.image}
                  alt={club.name || ""}
                  className="h-6 w-6 flex-shrink-0 rounded-md object-cover"
                  fallback={
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-[#E9EEF6] text-[10px] font-bold text-[#6B7280]">
                      {initialsOf(club.name)}
                    </div>
                  }
                />
                {club.name ? (
                  <span className="truncate text-[14px] font-semibold text-[#374151]">
                    {club.name}
                  </span>
                ) : null}
                {club.name && club.league ? (
                  <span aria-hidden="true" className="flex-shrink-0 text-[#9CA3AF]">
                    ·
                  </span>
                ) : null}
                {club.league ? (
                  <span className="truncate text-[14px] text-[#6B7280]">
                    {club.league}
                  </span>
                ) : null}
              </div>
            ) : null}

            {experienceYears !== null ? (
              // One string, not "{n} years" plus " of experience": the platform
              // translates RENDERED TEXT NODES, so a sentence split across two
              // nodes reaches the translator as two fragments and comes back
              // half English.
              <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-[#6B7280]">
                {`${formatYears(experienceYears)} of experience`}
              </p>
            ) : null}
          </div>
        </div>

        {/* Status cluster and, on a wide page, the actions */}
        <div
          className={
            compact
              ? "flex min-w-0 basis-full flex-col gap-2.5"
              : "ml-auto flex flex-col items-end gap-2.5"
          }
        >
          <div
            className={`flex flex-wrap items-center gap-2 ${
              compact ? "w-full" : "justify-end"
            }`}
          >
            {chip ? (
              <span
                className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-bold ${chip.className}`}
              >
                {chip.label}
              </span>
            ) : null}
            {scoutingCount > 0 ? (
              <span
                title="How often this pitch has been sent to scouting"
                className="whitespace-nowrap rounded-full bg-[#FEF3C7] px-2.5 py-1 text-[12px] font-bold text-[#B45309]"
              >
                Scouted {scoutingCount}/{scoutingMax}
              </span>
            ) : null}
            {timeLeft ? (
              <>
                <span className="whitespace-nowrap text-[13px] text-[#6B7280]">
                  Time left to response:
                </span>
                <span
                  className={`whitespace-nowrap rounded-full bg-gradient-to-r from-[#FDE5E5] to-[#FEF3C7] px-2.5 py-1 text-[13px] font-bold text-[#92400E] ${
                    compact ? "ml-auto" : ""
                  }`}
                >
                  {timeLeft}
                </span>
              </>
            ) : null}
            {isUndecided && canBanAgents ? (
              <BanAgentMenu pitch={pitch} onBan={handleBanAgent} />
            ) : null}
          </div>

          {!compact && showDecisionActions ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {decisionButtons}
              {acceptButton}
            </div>
          ) : null}
          {!compact && showInboxButton ? inboxButton : null}
        </div>
      </div>

      {/* Expired: the amber banner with the pick-up window, or the closed notice */}
      {isExpired && (canReopen || reopenWindowClosed) ? (
        canReopen ? (
          <div className="mt-3.5 flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-[10px] border border-[#FCD34D] bg-[#FEF3C7] px-3.5 py-3">
            <div className="min-w-0 flex-[1_1_220px]">
              <p className="m-0 text-[14px] font-bold text-[#92400E]">
                This pitch has expired
              </p>
              {reopenTimeLeft ? (
                <p className="m-0 mt-0.5 flex flex-wrap items-center gap-1 text-[13px] text-[#92400E]">
                  <span>Time left to pick this pitch up:</span>
                  <strong className="whitespace-nowrap">{reopenTimeLeft}</strong>
                </p>
              ) : null}
            </div>
            {canAcceptPitches ? (
              <button
                type="button"
                onClick={() => handleReopen(pitch)}
                title="Accept this pitch after all and open the chat with the agent"
                className="min-h-[46px] whitespace-nowrap rounded-[10px] bg-[#B45309] px-[18px] text-[14px] font-semibold text-white hover:bg-[#92400E]"
              >
                Reopen &amp; chat
              </button>
            ) : null}
          </div>
        ) : (
          <div className="mt-3.5 rounded-[10px] border border-[#E5E7EB] bg-[#F3F4F6] px-3.5 py-3">
            <p className="m-0 text-[14px] text-[#6B7280]">
              The 14-day window to pick this pitch up has closed.
            </p>
            <p className="m-0 mt-0.5 text-[14px] text-[#6B7280]">
              This pitch will be rejected automatically.
            </p>
          </div>
        )
      ) : null}

      {/* Facts */}
      <div className="mt-4 border-t border-[#F3F4F6] pt-3.5">
        {/*
          The VERDICT is asked first, the number second — and never the other
          way round. `free_agent` is the one rule that decides whether a pitch
          is fee-free (agent_pitch_logic.resolve_free_agent); a stored amount is
          only ever the answer to "how much", never to "is there a fee at all".

          Letting the amount go first, as this block used to, gave the fee a
          veto over the verdict: a LEGACY row — written before the amount
          stopped being stored — still carries a number, so the same pitch
          showed a price here and "Free Agent" in its own header badge and in
          the agent's inbox. The stored number is untouched in the database (no
          backfill); it is simply not shown where the verdict contradicts it.
        */}
        {isFreeAgent ? (
          <div className="flex items-center justify-between gap-4 rounded-[10px] border border-[#BCD8FE] bg-[#EFF6FF] px-3.5 py-3">
            <span className="flex-shrink-0 text-[14px] font-semibold text-[#1D4ED8]">
              Transfer fee
            </span>
            <span
              title="No transfer fee. Free agent player."
              className="min-w-0 break-words text-right text-[15px] font-bold text-[#1D4ED8]"
            >
              Free Agent · no fee required
            </span>
          </div>
        ) : transferFee ? (
          <div className="flex items-center justify-between gap-4 rounded-[10px] bg-[#F5F7FB] px-3.5 py-3">
            <span className="flex-shrink-0 text-[14px] font-semibold text-[#374151]">
              Transfer fee
            </span>
            <span className="min-w-0 break-words text-right text-[15px] font-bold text-[#111827]">
              {transferFee}
            </span>
          </div>
        ) : null}

        <div className="px-0.5 pt-1">
          {rows.map((row) => (
            <FactRow key={row.label} label={row.label} value={row.value} />
          ))}

          {agentName ? (
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 px-3 py-2">
              <span className="flex-shrink-0 text-[14px] font-medium text-[#6B7280]">
                Applied by agent
              </span>
              {/* The agent's own profile, when the payload names the account
                  the route resolves. Without it the very same value renders as
                  plain text — the club still reads who pitched, it just gets no
                  link that would go nowhere. */}
              {agentProfilePath ? (
                <button
                  type="button"
                  title="View agent profile"
                  onClick={() => openProfile(agentProfilePath)}
                  className="-mr-2 ml-auto flex min-h-[44px] min-w-0 items-center gap-2 rounded-lg px-2 text-right hover:bg-[#EFF6FF]"
                >
                  {agentValue}
                </button>
              ) : (
                <span className="ml-auto flex min-w-0 items-center gap-2">
                  {agentValue}
                </span>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {note ? (
        <div className="mt-2.5 rounded-[10px] border border-[#EEF1F6] bg-[#F8F9FB] px-3.5 py-3">
          <p className="m-0 mb-0.5 text-[11px] font-bold uppercase tracking-wider text-[#6B7280]">
            Note from agent
          </p>
          <p className="m-0 text-[14px] leading-relaxed text-[#374151]">{note}</p>
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-1.5">
        {pitch?.has_documents ? (
          <a
            href={pitch.document_url || pitch.document}
            target="_blank"
            rel="noopener noreferrer"
            className="-ml-2.5 inline-flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2.5 text-[14px] font-semibold text-[#2183FE] hover:bg-[#EFF6FF]"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className="flex-shrink-0"
            >
              <path
                d="M9.5 1.5H4.5C3.9 1.5 3.5 1.9 3.5 2.5V13.5C3.5 14.1 3.9 14.5 4.5 14.5H11.5C12.1 14.5 12.5 14.1 12.5 13.5V4.5L9.5 1.5Z"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
              <path
                d="M9.5 1.5V4.5H12.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
            </svg>
            Attached documents
          </a>
        ) : null}
        {/* One link, three destinations. The label and the route come from the
            SAME reader (`profileLinkOf`), so a card can never advertise "Coach
            profile" and open a player page. No identifier means no link at all
            rather than a link into nothing. */}
        {profileLink ? (
          <button
            type="button"
            onClick={() => openProfile(profileLink.path)}
            className="inline-flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2.5 text-[14px] font-semibold text-[#2183FE] hover:bg-[#EFF6FF]"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              aria-hidden="true"
              className="flex-shrink-0"
            >
              <path
                d="M5.5 2.5H2.5V11.5H11.5V8.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.5 2.5H11.5V5.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.5 2.5L6.5 7.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
            {profileLink.label}
          </button>
        ) : null}
      </div>

      {/* On a narrow page the actions live at the bottom, where a thumb is:
          Scouting and Reject share a row, Accept takes the full width so the
          two destructive-ish choices can never be hit by aiming for it. */}
      {compact && showDecisionActions ? (
        <div className="mt-3 flex flex-col gap-2.5">
          <div className="flex gap-2.5">{decisionButtons}</div>
          {acceptButton}
        </div>
      ) : null}
      {compact && showInboxButton ? <div className="mt-3">{inboxButton}</div> : null}
    </div>
  );
};

const EMPTY_LINES = {
  active: "You haven't received any pitches for this request yet.",
  scouting: "No pitches are in scouting for this request.",
  deal: "No deals available for this request.",
};

const ViewPitchesList = ({
  pitches,
  tender,
  activeTab,
  loading,
  error,
  formatPosition,
  canAcceptPitches,
  handleAcceptReject,
  handleReopen,
  handleScouting,
  handleBanAgent,
  canBanAgents,
  navigate,
  compact = false,
  filtersActive = false,
}) => {
  // The scouting confirmation lives here rather than in the page, because the
  // club, staff and coach pages all render this list and the dialog belongs to
  // the button that opens it.
  const [scoutingPitch, setScoutingPitch] = useState(null);
  const isStaff = isStaffTender(tender);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-[#DBEAFE] border-t-[#2183FE]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center px-4 py-14 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FEF2F2]">
          <span className="text-[22px] font-extrabold text-[#DC2626]">!</span>
        </div>
        <h3 className="mb-1 mt-4 text-[16px] font-bold text-[#111827]">
          Something went wrong
        </h3>
        <p className="m-0 max-w-[360px] text-[14px] text-[#6B7280]">{error}</p>
      </div>
    );
  }

  if (!pitches || pitches.length === 0) {
    return (
      <div className="flex flex-col items-center px-4 py-14 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#E9EEF6]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect
              x="3"
              y="5.5"
              width="18"
              height="13"
              rx="2"
              stroke="#6B7280"
              strokeWidth="1.6"
            />
            <path
              d="M3.5 7L12 13L20.5 7"
              stroke="#6B7280"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="mb-1 mt-4 text-[16px] font-bold text-[#111827]">
          No pitches found
        </h3>
        <p className="m-0 max-w-[360px] text-[14px] text-[#6B7280]">
          {filtersActive
            ? "No pitches match your filters. Reset the filters to see all pitches in this tab."
            : EMPTY_LINES[activeTab] ||
              `No ${activeTab} pitches available for this request.`}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {pitches.map((pitch) => (
          <PitchCard
            key={pitch.id}
            pitch={pitch}
            tender={tender}
            formatPosition={formatPosition}
            canAcceptPitches={canAcceptPitches}
            handleAcceptReject={handleAcceptReject}
            handleReopen={handleReopen}
            handleBanAgent={handleBanAgent}
            canBanAgents={canBanAgents}
            navigate={navigate}
            compact={compact}
            onRequestScouting={setScoutingPitch}
          />
        ))}
      </div>

      {/* Scouting now asks first. It is not a decision, but it spends one of
          three rounds and moves the card into another tab, and a card that
          vanishes on a single tap reads like the tap destroyed something. */}
      <ConfirmationModal
        isOpen={!!scoutingPitch}
        onClose={() => setScoutingPitch(null)}
        onConfirm={() => {
          const pitch = scoutingPitch;
          setScoutingPitch(null);
          if (pitch) handleScouting(pitch);
        }}
        title="Send this pitch to scouting?"
        message={
          scoutingPitch
            ? `You are about to send the pitch for ${
                (isStaff
                  ? scoutingPitch.staff_name
                  : scoutingPitch.player?.name) || "this candidate"
              } to scouting.`
            : ""
        }
        lines={
          scoutingPitch
            ? [
                isStaff
                  ? "The deadline is extended: you get 3 more days to look at the candidate."
                  : "The deadline is extended: you get 3 more days to look at the player.",
                `You can send a pitch to scouting up to ${
                  scoutingPitch.scouting_max ?? 3
                } times. This pitch has used ${scoutingPitch.scouting_count || 0} of ${
                  scoutingPitch.scouting_max ?? 3
                }.`,
                "The agent is not notified.",
              ]
            : []
        }
        confirmText="Send to scouting"
        confirmButtonClass="bg-[#B45309] hover:bg-[#92400E]"
        variant="v2"
      />
    </>
  );
};

export default ViewPitchesList;
